/**
 * Product card extras, switched on per section (featured collection, collection grid):
 *
 * <card-slideshow>  All product images in a scroll-snap track. Hover devices get
 *                   prev/next arrows, touch devices swipe and see dots. Arrows use the
 *                   browser's smooth scroll (instant with reduced motion), like the
 *                   theme's other sliders. With "Show second image on hover" the second
 *                   image covers the first slide while a mouse hovers the card (CSS) and
 *                   counts as position 2: "next" goes to image 3, "previous" back to 1.
 *                   When the mouse leaves, the card resets to image 1.
 *
 * <card-quick-add>  "Add to cart" reveals one button per value of the product's only
 *                   option. Each button carries its variant ID; on submit the ID goes
 *                   into the form's hidden `id` input and Dawn's <product-form> does the
 *                   rest, so the cart notification and count bubble update exactly as on
 *                   the product page. Nothing here reads option names or values, so it
 *                   works in every language.
 */
if (!customElements.get('card-slideshow')) {
  customElements.define(
    'card-slideshow',
    class CardSlideshow extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.card-slideshow__track');
        this.slides = this.track ? Array.from(this.track.children) : [];
        this.prevButton = this.querySelector('.card-slideshow__arrow--prev');
        this.nextButton = this.querySelector('.card-slideshow__arrow--next');
        if (this.slides.length < 2 || !this.prevButton || !this.nextButton || this.initialised) return;
        this.initialised = true;

        this.dots = Array.from(this.querySelectorAll('.card-slideshow__dot'));
        this.index = 0;
        this.target = null;
        this.hoverImage = this.querySelector('.card-slideshow__hover img');
        this.hoverQuery = window.matchMedia('(hover: hover) and (min-width: 990px)');

        this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
        this.prevButton.addEventListener('click', (event) => this.step(event, -1));
        this.nextButton.addEventListener('click', (event) => this.step(event, 1));

        // Fetch the next image before it is needed: on first hover or first touch.
        const card = this.closest('.card-wrapper') || this;
        this.card = card;
        card.addEventListener('pointerenter', () => this.preload(this.index + 1), { once: true });
        if (this.hoverImage) {
          card.addEventListener('pointerenter', () => (this.hoverImage.loading = 'eager'), { once: true });
          // The hover image is showing now: the previous arrow leads back to image 1.
          card.addEventListener('pointerenter', () => requestAnimationFrame(() => this.refreshArrows()));
          card.addEventListener('pointerleave', (event) => this.resetAfterHover(event));
        }
        this.track.addEventListener('touchstart', () => this.preload(this.index + 1), { once: true, passive: true });

        this.update(this.currentIndex());
      }

      currentIndex() {
        const width = this.track.clientWidth;
        if (!width) return 0;
        return Math.max(0, Math.min(this.slides.length - 1, Math.round(this.track.scrollLeft / width)));
      }

      step(event, delta) {
        event.preventDefault();
        event.stopPropagation();
        let from = this.target !== null ? this.target : this.currentIndex();
        if (this.hoverImageShowing()) {
          // The hover image is image 2. Count from there; going forward, first move the
          // track under it (unseen) so the scroll animates from image 2 to image 3.
          from = 1;
          if (delta > 0) this.track.scrollTo({ left: this.track.clientWidth, behavior: 'auto' });
        }
        this.classList.add('is-browsing');
        this.goTo(from + delta);
      }

      /** Mouse left the card: back to image 1, so the next hover shows image 2 again. */
      resetAfterHover(event) {
        // Touch fires pointerleave when the finger lifts; never undo a swipe.
        if (event.pointerType === 'touch' || !this.hoverQuery.matches) return;
        this.classList.remove('is-browsing');
        clearTimeout(this.targetTimer);
        this.target = null;
        if (this.track.scrollLeft !== 0) this.track.scrollTo({ left: 0, behavior: 'auto' });
        this.update(0);
      }

      hoverImageShowing() {
        return (
          Boolean(this.hoverImage) &&
          this.hoverQuery.matches &&
          this.index === 0 &&
          !this.classList.contains('is-browsing') &&
          this.card.matches(':hover')
        );
      }

      goTo(index) {
        const target = Math.max(0, Math.min(this.slides.length - 1, index));
        this.target = target;
        this.preload(target);
        this.preload(target + 1);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({ left: target * this.track.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
        this.update(target);
        // Give up on the target if the scroll never gets there (interrupted by a swipe).
        clearTimeout(this.targetTimer);
        this.targetTimer = setTimeout(() => {
          this.target = null;
          this.update(this.currentIndex());
        }, 1000);
      }

      onScroll() {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          const current = this.currentIndex();
          if (this.target !== null) {
            if (current !== this.target) return;
            this.target = null;
            clearTimeout(this.targetTimer);
          }
          if (current !== this.index) {
            this.preload(current + 1);
            this.update(current);
          }
        });
      }

      preload(index) {
        const slide = this.slides[index];
        const image = slide && slide.querySelector('img[loading="lazy"]');
        if (image) image.loading = 'eager';
      }

      update(index) {
        this.index = index;
        this.classList.toggle('is-moved', index !== 0);
        this.dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
        this.refreshArrows();
      }

      refreshArrows() {
        // While the hover image shows, the card is visually at image 2.
        const position = this.hoverImageShowing() ? 1 : this.index;
        this.setDisabled(this.prevButton, position === 0, this.nextButton);
        this.setDisabled(this.nextButton, position === this.slides.length - 1, this.prevButton);
      }

      setDisabled(button, disabled, other) {
        if (button.disabled === disabled) return;
        // A disabled arrow can't hold focus; keep keyboard focus on the card's other arrow.
        if (disabled && document.activeElement === button && !other.disabled) other.focus();
        button.disabled = disabled;
      }
    }
  );
}

if (!customElements.get('card-quick-add')) {
  customElements.define(
    'card-quick-add',
    class CardQuickAdd extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.form = this.querySelector('form');
        this.productForm = this.querySelector('product-form');
        this.idInput = this.form && this.form.querySelector('input[name="id"]');
        this.toggle = this.querySelector('.card-quick-add__toggle');
        this.sizes = this.querySelector('.card-quick-add__sizes');
        this.card = this.closest('.card-wrapper') || this;
        this.errorWrapper = this.querySelector('.card-quick-add__error');

        // Capture phase on this element runs before <product-form>'s own submit handler.
        this.addEventListener('submit', this.onSubmit.bind(this), true);
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button[type="submit"]');
          if (button) this.lastClicked = button;
        });

        if (!this.toggle || !this.sizes) return;

        this.toggle.addEventListener('click', () => this.open());
        this.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && this.isOpen) {
            event.stopPropagation();
            this.close(true);
          }
        });
        this.addEventListener('focusout', (event) => {
          if (this.busy || !this.isOpen) return;
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          // Focus left the card's controls (keyboard or a click elsewhere).
          if (event.relatedTarget) this.close(false);
        });
        this.card.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse' || this.busy || !this.isOpen) return;
          const focusInside = this.contains(document.activeElement);
          this.close(false);
          if (focusInside) document.activeElement.blur();
        });
        this.onDocumentPointerDown = (event) => {
          if (this.busy || !this.isOpen || this.contains(event.target)) return;
          this.close(false);
        };
      }

      disconnectedCallback() {
        if (this.onDocumentPointerDown) document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        if (this.observer) this.observer.disconnect();
      }

      get isOpen() {
        return this.sizes && !this.sizes.hidden;
      }

      open() {
        if (!this.sizes || this.toggle.disabled) return;
        this.sizes.hidden = false;
        this.toggle.setAttribute('aria-expanded', 'true');
        this.classList.add('is-open');
        document.addEventListener('pointerdown', this.onDocumentPointerDown);
        const first = this.sizes.querySelector('button:not([disabled])');
        if (first) first.focus();
      }

      close(returnFocus) {
        if (!this.sizes) return;
        const focusInside = this.sizes.contains(document.activeElement);
        this.sizes.hidden = true;
        this.toggle.setAttribute('aria-expanded', 'false');
        this.classList.remove('is-open');
        document.removeEventListener('pointerdown', this.onDocumentPointerDown);
        if (this.errorWrapper) this.errorWrapper.hidden = true;
        if (returnFocus || focusInside) this.toggle.focus({ preventScroll: true });
      }

      onSubmit(event) {
        const button = event.submitter || this.lastClicked;
        const productForm = this.productForm;
        const ready = productForm && customElements.get('product-form') && 'submitButton' in productForm;

        if (
          this.busy ||
          !button ||
          button.disabled ||
          button.getAttribute('aria-disabled') === 'true' ||
          !button.dataset.variantId
        ) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }

        this.idInput.value = button.dataset.variantId;
        this.idInput.disabled = false;
        if (!ready) return; // Without <product-form> the form posts to /cart/add as normal.

        // Point <product-form> at the pressed button so its loading state and sold-out
        // message show there, and move the one spinner into it.
        productForm.submitButton = button;
        productForm.submitButtonText = button.querySelector('span');
        const spinner = this.querySelector('.loading__spinner');
        if (spinner && spinner.parentElement !== button) button.appendChild(spinner);

        this.busy = true;
        this.watch(button);
      }

      watch(button) {
        if (this.observer) this.observer.disconnect();
        let started = false;
        this.observer = new MutationObserver(() => {
          const loading = button.classList.contains('loading');
          if (loading) {
            started = true;
            return;
          }
          if (!started) return;
          this.observer.disconnect();
          this.busy = false;
          this.done(button);
        });
        this.observer.observe(button, { attributes: true, attributeFilter: ['class'] });
        // product-form adds `loading` synchronously; if it did not run, release the lock.
        requestAnimationFrame(() => {
          if (!started && !button.classList.contains('loading')) {
            this.observer.disconnect();
            this.busy = false;
          }
        });
      }

      done(button) {
        const failed = this.productForm.error || button.getAttribute('aria-disabled') === 'true';
        if (failed || !this.sizes) return;
        const cart = this.productForm.cart;
        // Closing the cart notification returns focus to the "Add to cart" button, not to
        // the size button that is about to be hidden.
        if (cart && typeof cart.setActiveElement === 'function' && cart.activeElement === button) {
          cart.setActiveElement(this.toggle);
        }
        this.close(false);
      }
    }
  );
}
